import type { AdvDisLayerSourceInput, AdvDisSourceNode, AdvDisSourceEntry, AdvDisSourceInventory } from '../types';
import { collectAdvDisLayerSources } from './adv-dis-layer-sources';
import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { readItemField } from './item-field-reader';
import { readInventoryField, readItemUuidSlot, readLayerListEntryUuid } from './inventory-raw-readers';
import { readNodeItemId, readOverrideEntryAbilityId } from './layer-cell-readers';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';
import { createLayerEntriesReader } from './layer-entries-reader';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { readCreaturePayloadStatuses, readCreatureStatuses } from './creature-status-reader';
import { resolveLayerEntry } from './layer-entry-resolver';
import { phpJsonTruthy } from './php-json-values';

export interface AdvDisSourceReaderDependencies extends Required<ItemFieldDependencies> {
    catalogRows: ProviderCatalogRows & Readonly<Record<string, { readonly aggregation_strategy?: string | null }>>;
    abilityTypes: readonly string[];
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    queryDefinition: (id: string) => ItemFieldRow | null;
    /** Optional local evidence hook. Never includes a full input row. */
    trace?: (event: readonly unknown[]) => void;
}
export type RawLayerSource = Readonly<Record<string, unknown>>;
function arrayLike(value: unknown): value is ItemFieldRow { return value !== null && typeof value === 'object'; }
function own(value: unknown, key: string): unknown {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
function path(value: unknown, ...keys: string[]): unknown { for (const key of keys) value = own(value, key); return value; }
const values = (value: unknown): unknown[] => arrayLike(value) ? Object.values(value) : [];
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function trigger(row: ItemFieldRow): boolean {
    return [...Object.keys(row), ...Object.keys(arrayLike(own(row, 'item_layer_types')) ? own(row, 'item_layer_types') as ItemFieldRow : {})]
        .some(key => key.includes('trigger'));
}
const sections = ['creature_type_arr', 'creature_sub_type_arr', 'lineage_arr', 'class_arr', 'subclass_arr', 'background_arr'] as const;
const buckets = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability'];

/** Held source views over the native scalar/list reader composition. One factory per
 * compile request preserves provider memo lifetime and shared scalar/list inventory guards.
 * External callbacks supply resolved results only; this factory never fetches or writes state.
 */
export function createAdvDisSourceReader(dependencies: AdvDisSourceReaderDependencies): {
    sources: (row: ItemFieldRow, layer: string, numericPresence?: boolean) => RawLayerSource[];
    statuses: (row: ItemFieldRow | null, mode?: string) => ItemFieldRow;
} {
    const emit = (event: readonly unknown[]): void => { dependencies.trace?.(event); };
    const reference = (id: string): ItemFieldRow | null => { emit(['reference', id]); return dependencies.resolveCarrierRef(id); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({ catalogRows: dependencies.catalogRows, resolveCarrierRef: reference, nodeItemId });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const inventoryContext = dependencies.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return dependencies.resolveInventoryInstance(id); });
    const scalar = createLayerValueReader({ nodeItemId, providers, inventoryContext });
    const lists = createLayerEntriesReader({ nodeItemId, providers, inventoryContext, abilityTypes: dependencies.abilityTypes,
        aggregationStrategy: name => {
            const strategy = own(own(dependencies.catalogRows, name), 'aggregation_strategy') ?? 'host_wins';
            emit(['strategy', name, strategy]); return strategy as string;
        },
        overrideEntryAbilityId: entry => { const id = readOverrideEntryAbilityId(entry); emit(['override', id]); return id; },
    });
    const fields: ItemFieldDependencies = { resolveCarrierRef: reference,
        resolveItemName: id => { emit(['name', id]); return dependencies.resolveItemName(id); },
        resolveInventoryIdentity: id => { emit(['inventory-identity', id]); return dependencies.resolveInventoryIdentity(id); },
    };
    const field = (row: ItemFieldRow, key: string): unknown => readItemField(row, key, fields);
    const statuses = (row: ItemFieldRow | null, mode = 'target'): ItemFieldRow => mode === 'target'
        ? readCreaturePayloadStatuses(row, scalar.read, reference)
        : readCreatureStatuses(row, mode, reference);
    function sources(caster: ItemFieldRow, layer: string, numericPresence = false): RawLayerSource[] {
        if (!arrayLike(caster)) throw new TypeError('Source reader requires an array');
        // Views defer reads until the collector needs them. No member/inventory copy or fetch.
        const node = (row: () => ItemFieldRow): AdvDisSourceNode => ({
            get itemId() { return field(row(), 'item_id') as string | null; },
            get itemName() { return field(row(), 'item_name') as string | null; },
            get rootLayer() { return own(row(), layer); },
            get layerValue() { return scalar.read(row(), layer); },
            get abilityId() { return scalar.read(row(), 'ability_id') as string | null; },
            get abilityName() { return scalar.read(row(), 'ability_name') as string | null; },
            get hasTrigger() { return trigger(row()); },
        });
        const resolve = (entry: unknown, required = false): unknown => resolveLayerEntry(entry, caster,
            id => {
                emit(['definition', id]);
                const definition = dependencies.queryDefinition(id);
                if (required && definition === null) throw new AdvDisSourceDefinitionNotHeld(id);
                return definition;
            }, required);
        const ability = (entry: unknown): AdvDisSourceEntry | null => {
            if (!arrayLike(entry) && typeof entry !== 'string') return null;
            const wrapped = own(entry, 'item_arr') ?? entry;
            let resolved: ItemFieldRow | undefined;
            const row = (): ItemFieldRow => {
                if (resolved) return resolved;
                const definition = arrayLike(wrapped) && arrayLike(own(wrapped, 'item_layer_types'))
                    ? wrapped : resolve(wrapped, true);
                if (!arrayLike(definition)) {
                    const id = readLayerListEntryUuid(wrapped);
                    if (id) throw new AdvDisSourceDefinitionNotHeld(id);
                    return {};
                }
                if (readLayerListEntryUuid(wrapped) && !arrayLike(own(definition, 'item_layer_types'))
                    && !arrayLike(own(definition, 'items'))) throw new AdvDisSourceDefinitionNotHeld(readLayerListEntryUuid(wrapped));
                resolved = definition;
                return resolved;
            };
            return { node: node(row), passiveConditionsActive: own(entry, 'passive_conditions_active') };
        };
        const gear = (inventory: unknown): AdvDisSourceInventory | null => {
            if (!arrayLike(inventory)) return null;
            return { node: node(() => inventory),
                get equippedToken() { return readItemUuidSlot(readInventoryField(inventory, 'equipped') ?? ''); },
                get inventoryId() { return readInventoryField(inventory, 'inventory_id') as string | null; },
                get inventoryName() { return readInventoryField(inventory, 'inventory_name') as string | null; },
                get pictureUrls() { return { thumbnail: path(inventory, 'pictures', 'inventory_thumbnail_picture', '0', 'picture_url'),
                    presentation: path(inventory, 'pictures', 'inventory_presentation_picture', '0', 'picture_url'),
                    item: path(inventory, 'pictures', 'item', '0', 'picture_url') }; },
                get enchantmentLists() { return values(path(inventory, 'computed', 'enchantment', 'abilities'))
                    .map(list => arrayLike(list) ? values(list).map(ability) : null); },
                get legacyEnchantments() { const list = own(inventory, 'enchantment'); return arrayLike(list) ? values(list).map(ability) : null; },
            };
        };
        const input: AdvDisLayerSourceInput = {
            structured: Object.fromEntries(sections.map(section => [section, values(own(caster, section)).map(entry => {
                let resolved: unknown;
                const row = () => { resolved ??= resolve(entry); return arrayLike(resolved) ? resolved : {}; };
                return node(row);
            })])),
            get inventory() { return values(own(caster, 'inventory')).map(gear); },
            abilities: Object.defineProperties({}, Object.fromEntries(buckets.map(bucket => [bucket, {
                get: () => values(lists.read(caster, bucket)).map(ability),
            }]))) as AdvDisLayerSourceInput['abilities'],
            get statuses() { return values(statuses(caster)).map(entry => ({
                entry: node(() => arrayLike(entry) ? entry : {}),
                definition: arrayLike(own(entry, 'status_arr')) ? node(() => own(entry, 'status_arr') as ItemFieldRow) : null,
                statusId: own(entry, 'status_id') as string | null,
            })); },
            get legacyEquipped() { return values(own(caster, 'inventory_equipped')).map(inventory => arrayLike(inventory)
                ? { node: node(() => inventory), inventoryId: readInventoryField(inventory, 'inventory_id') as string | null } : null); },
        };
        // Only descriptors are detached; held member/catalog rows retain their identity.
        return clone(collectAdvDisLayerSources(input, numericPresence)) as RawLayerSource[];
    }
    return { sources, statuses };
}

export class AdvDisSourceDefinitionNotHeld extends Error {
    readonly reason = 'rxsources31:source-definition-not-held';
    constructor(readonly id: string) { super(`rxsources31:source-definition-not-held:${id}`); }
}
